import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../supabaseClient'
import Papa from 'papaparse'

function normalise(row) {
  const g = k => {
    const found = Object.keys(row).find(h => h.trim().toLowerCase() === k.toLowerCase())
    return found ? (row[found] || '').toString().trim() : ''
  }
  function cleanPhone(val) {
    if (!val) return null
    if (/e\+/i.test(val)) {
      try { return Math.round(parseFloat(val)).toString() } catch { return val.replace(/\D/g,'') || null }
    }
    const clean = val.replace(/\D/g, '')
    return clean || null
  }
  // Accepts our own export columns and Meta Lead Ads exports (first_name,
  // whatsapp_number, phone_number, created_time, ad_name, campaign_name…).
  const phone    = cleanPhone(g('Phone') || g('phone_number') || g('phone number'))
  const whatsapp = cleanPhone(g('WhatsApp number') || g('whatsapp') || g('whatsapp number') || g('whatsapp_number')) || phone
  const joined   = [g('first_name') || g('first name'), g('last_name') || g('last name')].filter(Boolean).join(' ')
  const fullName = g('Name') || g('full_name') || g('full name') || joined || 'Unknown'

  const metaCreated = g('created_time')
  const createdDate = g('Created') ||
    (metaCreated && !isNaN(new Date(metaCreated)) ? new Date(metaCreated).toLocaleDateString('en-IN') : '') ||
    new Date().toLocaleDateString('en-IN')

  // Course from an explicit column, else from the ad/ad set name ("Guitar Ads").
  const adText = [g('ad_name'), g('adset_name'), g('campaign_name'), g('form_name')].join(' ')
  const course = g('Course') || g('Course Interest') || g('Subject') ||
    COURSE_KEYWORDS.find(([re]) => re.test(adText))?.[1] || null

  const isMeta = !!(g('ad_id') || g('form_id') || g('campaign_name'))
  const platform = g('platform').toLowerCase()
  // The form's own questions (demo time, learner age…) are whatever columns
  // aren't part of Meta's standard export.
  const customAnswers = Object.keys(row)
    .filter(h => !META_EXPORT_COLUMNS.has(h.trim().toLowerCase()) && (row[h] || '').toString().trim())
    .map(h => `${h.trim()}: ${row[h].toString().trim()}`)
  const metaNotes = isMeta ? [
    g('state') && `State: ${g('state')}`,
    g('city') && `City: ${g('city')}`,
    ...customAnswers,
    g('campaign_name') && `Campaign: ${g('campaign_name')}`,
    g('ad_name') && `Ad: ${g('ad_name')}`,
    g('id') && `Meta lead ID: ${g('id')}`,
  ].filter(Boolean).join(' · ') : ''

  return {
    full_name:       fullName,
    created_date:    createdDate,
    email:           g('Email') || null,
    source:          g('Source') || (platform === 'ig' ? 'Instagram Ads' : 'Facebook Ads'),
    channel:         g('Channel') || null,
    stage:           g('Stage') || 'Intake',
    owner:           g('Owner') || 'Unassigned',
    labels:          g('Labels') || null,
    phone:           phone || whatsapp,
    secondary:       g('Secondary') || null,
    whatsapp_number: whatsapp,
    course_interest: course,
    ...(metaNotes ? { notes: metaNotes } : {}),
    status:          'New',
    import_date:     new Date().toISOString().slice(0, 10),
  
  }
}

const META_EXPORT_COLUMNS = new Set([
  'id', 'created_time', 'ad_id', 'ad_name', 'adset_id', 'adset_name', 'campaign_id', 'campaign_name',
  'form_id', 'form_name', 'is_organic', 'platform', 'lead_status', 'full_name', 'full name', 'first_name',
  'first name', 'last_name', 'last name', 'name', 'email', 'phone', 'phone_number', 'phone number',
  'whatsapp', 'whatsapp number', 'whatsapp_number', 'state', 'city', 'created', 'source', 'channel',
  'stage', 'owner', 'labels', 'secondary', 'course', 'course interest', 'subject',
])

// What a lead answered on the form, as [{ label, value }]: every question
// from their Meta lead form (meta_leads.raw_field_data) when we have it,
// otherwise the "Key: value" parts of the lead's notes (CSV imports,
// website bookings). Name/contact/course answers already have columns.
const ANSWER_LABELS = { preferred_time: 'Demo time', learner_age: 'Age' }
const OPTION_LABELS = {
  guitar: 'Guitar', piano: 'Piano / Keyboard', chess: 'Chess',
  age_5_8: '5–8 years', age_9_12: '9–12 years', age_13_17: '13–17 years', adult: 'Adult (18+)',
}
const NOT_AN_ANSWER = /name|email|phone|whatsapp|course|interest|instrument|^campaign$|^ad$|meta lead id/i
const prettyLabel = key => ANSWER_LABELS[key] ||
  key.replace(/[_?]+/g, ' ').trim().replace(/^\w/, c => c.toUpperCase())

function formAnswers(lead, rawFieldData) {
  if (rawFieldData?.length) {
    return rawFieldData
      .filter(f => f.name && !NOT_AN_ANSWER.test(f.name) && f.values?.length)
      .map(f => ({ label: prettyLabel(f.name), value: f.values.map(v => OPTION_LABELS[v] || v).join(', ') }))
  }
  return (lead.notes || '').split(/\n| · /)
    .map(part => part.match(/^([^:]{1,60}):\s*(.+)$/))
    .filter(m => m && !NOT_AN_ANSWER.test(m[1].trim()))
    .map(m => ({ label: m[1].trim(), value: m[2].trim() }))
}

const metaLeadId = lead => lead.notes?.match(/Meta lead ID: (\d+)/)?.[1]

const COURSE_KEYWORDS = [
  [/guitar/i, 'Guitar'], [/piano|keyboard/i, 'Keyboard'], [/vocal|singing/i, 'Vocals'],
  [/tabla/i, 'Tabla'], [/dance/i, 'Dance'], [/chess/i, 'Chess'], [/public\s*speaking/i, 'Public Speaking'],
]

function formatDate(d) {
  if (!d) return '—'
  try {
    const dt = new Date(d)
    if (isNaN(dt)) return d
    return dt.toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })
  } catch { return d }
}

function groupByDate(leads) {
  const map = {}
  leads.forEach(l => {
    const key = l.import_date || 'Unknown'
    if (!map[key]) map[key] = []
    map[key].push(l)
  })
  return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]))
}

const STATUS_META = {
  New:            { label:'New',            color:'#60a5fa', bg:'rgba(96,165,250,0.12)',   dot:'#3b82f6' },
  Contacted:      { label:'Contacted',      color:'#fbbf24', bg:'rgba(251,191,36,0.12)',   dot:'#f59e0b' },
  'Demo Requested':{ label:'Demo Requested',color:'#fb923c', bg:'rgba(251,146,60,0.12)',   dot:'#f97316' },
  'Demo Payment Received':{ label:'Paid ₹199: book demo', color:'#34d399', bg:'rgba(52,211,153,0.12)', dot:'#10b981' },
  'Demo Scheduled':{ label:'Demo Scheduled',color:'#f4a335', bg:'rgba(244,163,53,0.12)',   dot:'#f59e0b' },
  'Demo Done':    { label:'Demo Done',      color:'#2dd4bf', bg:'rgba(45,212,191,0.12)',   dot:'#14b8a6' },
  Enrolled:       { label:'Enrolled',       color:'#a78bfa', bg:'rgba(139,92,246,0.12)',   dot:'#8b5cf6' },
  Converted:      { label:'Converted',      color:'#34d399', bg:'rgba(52,211,153,0.12)',   dot:'#10b981' },
  Lost:           { label:'Lost',           color:'#f87171', bg:'rgba(248,113,113,0.12)',  dot:'#ef4444' },
  'Dead/Unresponsive': { label:'Dead/Unresponsive', color:'#6b7280', bg:'rgba(107,114,128,0.12)', dot:'#4b5563' },
}

// How far down the funnel a lead's status means it got. Lost and
// Dead/Unresponsive count only as a lead — we don't know how far they got.
const FUNNEL = ['Leads', 'Contacted', 'Demo requested', 'Demo done', 'Enrolled']
function funnelStep(status) {
  const s = status || 'New'
  if (s === 'Enrolled' || s === 'Converted') return 4
  if (s === 'Demo Done') return 3
  if (s.startsWith('Demo')) return 2 // Demo Requested (…), Demo Scheduled
  if (s === 'Contacted') return 1
  return 0
}

// The ad a lead came from ("Ad: …" in a Meta lead's notes), else its source.
function adOf(lead) {
  const m = (lead.notes || '').match(/(?:^|·\s*)Ad: ([^·\n]+)/)
  return m ? m[1].trim() : (lead.source || 'Other')
}

const COUNTRY_CODES = [['91', 'India'], ['44', 'UK'], ['971', 'UAE'], ['61', 'Australia'], ['1', 'US/Canada']]
const countryOf = phone => COUNTRY_CODES.find(([code]) => (phone || '').startsWith(code))?.[1] || 'Other'

// Old leads worth one "pick a time for your free demo" WhatsApp: never got
// past Contacted, not marked Lost/Dead, have a number, older than 2 days, and
// not already sent one (the server also refuses to send twice).
const REVIVE_MESSAGE = 'Hi {name}! 👋 Thanks for your interest in {course} at UniEDD Music & Arts Academy 🎵. What date and time works best for a free demo class? Just reply here with your preferred slot and we\'ll confirm it!'
function canRevive(lead) {
  const twoDaysAgo = new Date(Date.now() - 2 * 86400000).toISOString().slice(0, 10)
  return funnelStep(lead.status) <= 1 && !['Lost', 'Dead/Unresponsive'].includes(lead.status) &&
    !!(lead.whatsapp_number || lead.phone) && (lead.import_date || '9999') < twoDaysAgo &&
    !/free demo reminder (not )?sent \(old lead\)/.test(lead.notes || '')
}

const S = {
  wrap:   { fontFamily:"'DM Sans', sans-serif" },
  card:   { background:'rgba(var(--fg-rgb),0.03)', border:'0.5px solid rgba(var(--fg-rgb),0.08)', borderRadius:'16px', padding:'20px' },
  metric: { background:'rgba(var(--fg-rgb),0.03)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'16px' },
  badge:  (s) => ({ display:'inline-flex', alignItems:'center', gap:'5px', padding:'3px 10px', borderRadius:'20px', fontSize:'10px', fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', background: STATUS_META[s]?.bg || 'rgba(var(--fg-rgb),0.07)', color: STATUS_META[s]?.color || '#aaa' }),
  th:     { fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.1em', textTransform:'uppercase', padding:'8px 12px', textAlign:'left', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.07)', whiteSpace:'nowrap' },
  td:     { fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a75))', padding:'10px 12px', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.04)', verticalAlign:'middle' },
  btn:    (variant='primary') => ({
    padding: variant==='sm' ? '6px 14px' : '11px 22px',
    borderRadius:'10px', fontSize: variant==='sm' ? '11px' : '13px',
    fontWeight:700, cursor:'pointer', fontFamily:"'DM Sans', sans-serif", border:'none',
    background: variant==='danger'  ? 'rgba(239,68,68,0.15)'
              : variant==='warning' ? 'rgba(244,163,53,0.15)'
              : variant==='ghost'   ? 'rgba(var(--fg-rgb),0.06)'
              : 'linear-gradient(135deg,#1e90ff,#0066cc)',
    color:      variant==='danger'  ? '#f87171'
              : variant==='warning' ? '#f4a335'
              : variant==='ghost'   ? 'rgba(var(--fg-rgb),var(--a50))'
              : '#fff',
    transition:'all 0.15s',
  }),
}

// ── Find duplicates by phone or email ───────────────────────────────────────
function findDuplicates(leads) {
  const phoneMap = {}
  const emailMap = {}
  const dupIds   = new Set()

  leads.forEach(l => {
    if (l.phone) {
      if (phoneMap[l.phone]) { dupIds.add(l.id); dupIds.add(phoneMap[l.phone]) }
      else phoneMap[l.phone] = l.id
    }
    if (l.email) {
      if (emailMap[l.email]) { dupIds.add(l.id); dupIds.add(emailMap[l.email]) }
      else emailMap[l.email] = l.id
    }
  })
  return dupIds
}

// Which ads bring students, not just leads: per ad (or source), how many
// leads reached each funnel step. Only as good as the statuses the team sets.
function ResultsByAd({ leads }) {
  const [days, setDays] = useState(30)
  const [open, setOpen] = useState(true)
  const since = days ? new Date(Date.now() - days * 86400000).toISOString().slice(0, 10) : ''
  const inPeriod = leads.filter(l => !since || (l.import_date || '') >= since)
  const byAd = {}
  for (const l of inPeriod) {
    const row = byAd[adOf(l)] || (byAd[adOf(l)] = [0, 0, 0, 0, 0])
    for (let i = 0; i <= funnelStep(l.status); i++) row[i]++
  }
  const total = [0, 0, 0, 0, 0]
  Object.values(byAd).forEach(r => r.forEach((n, i) => { total[i] += n }))
  const rows = Object.entries(byAd).sort((a, b) => b[1][0] - a[1][0])
  const pct = (n, of) => of ? `${Math.round(n / of * 100)}%` : '—'

  return (
    <div style={{ ...S.card, marginBottom:'16px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
        <div style={{ flex:1, minWidth:'200px', cursor:'pointer' }} onClick={() => setOpen(o => !o)}>
          <div style={{ fontSize:'13px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a70))' }}>📈 Results by ad {open ? '▲' : '▼'}</div>
          <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', marginTop:'2px' }}>
            {total[0]} leads → {total[2]} demo requests → {total[3]} demos done → <b style={{ color:'#a78bfa' }}>{total[4]} students</b> ({pct(total[4], total[0])}). Keep each lead's status up to date so this stays accurate.
          </div>
        </div>
        <select value={days} onChange={e => setDays(+e.target.value)} style={{ ...S.btn('ghost'), padding:'6px 10px', fontSize:'12px' }}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={0}>All time</option>
        </select>
      </div>
      {open && rows.length > 0 && (
        <div style={{ overflowX:'auto', marginTop:'12px' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'560px' }}>
            <thead><tr>{['Ad / source', ...FUNNEL, 'Lead → student'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map(([ad, r]) => (
                <tr key={ad}>
                  <td style={{ ...S.td, fontWeight:600 }}>{ad}</td>
                  {r.map((n, i) => <td key={i} style={{ ...S.td, fontFamily:"'DM Mono',monospace" }}>{n}{i > 0 && n > 0 && <span style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))' }}> {pct(n, r[0])}</span>}</td>)}
                  <td style={{ ...S.td, fontWeight:700, color: r[4] ? '#a78bfa' : 'rgba(var(--fg-rgb),var(--a30))' }}>{pct(r[4], r[0])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// Admin: one WhatsApp "pick a time for your free demo" to old leads who
// never booked, newest first, in small batches the admin chooses.
function ReviveOldLeads({ leads, onDone }) {
  const [count, setCount] = useState(25)
  const [state, setState] = useState(null)
  const eligible = leads.filter(canRevive)
    .sort((a, b) => (b.import_date || '').localeCompare(a.import_date || ''))
  const batch = eligible.slice(0, count || eligible.length)
  const countries = {}
  batch.forEach(l => { const c = countryOf((l.whatsapp_number || l.phone || '').replace(/\D/g, '')); countries[c] = (countries[c] || 0) + 1 })
  const breakdown = Object.entries(countries).sort((a, b) => b[1] - a[1]).map(([c, n]) => `${n} ${c}`).join(' · ')

  async function send() {
    if (!batch.length) return
    if (!window.confirm(`Send the free-demo WhatsApp message to ${batch.length} old lead${batch.length !== 1 ? 's' : ''} (${breakdown})?\n\nEach person gets it only once. WhatsApp charges a small fee per message delivered.`)) return
    const tally = { sent: 0, skipped: 0, failed: 0 }
    try {
      const { data: { session } } = await supabase.auth.getSession()
      for (let i = 0; i < batch.length; i += 20) {
        setState({ busy: true, text: `Sending… ${i} of ${batch.length}` })
        const res = await fetch('/api/meta', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
          body: JSON.stringify({ action: 'revive-leads', leadIds: batch.slice(i, i + 20).map(l => l.id) }),
        })
        const r = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(r.error || `Failed (${res.status})`)
        Object.values(r).forEach(v => { if (v === 'sent') tally.sent++; else if (String(v).startsWith('skipped')) tally.skipped++; else tally.failed++ })
      }
      setState({ ok: true, text: `✓ ${tally.sent} sent${tally.skipped ? ` · ${tally.skipped} skipped` : ''}${tally.failed ? ` · ${tally.failed} not delivered (see WhatsApp failures)` : ''}. Replies with a time mark the lead Demo Requested and alert you on WhatsApp.` })
    } catch (err) {
      setState({ ok: false, text: `${err.message} — ${tally.sent} sent before it stopped.` })
    }
    onDone()
  }

  return (
    <div style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap', padding:'12px 16px', background:'rgba(52,211,153,0.06)', border:'0.5px solid rgba(52,211,153,0.2)', borderRadius:'12px', marginBottom:'16px' }}>
      <div style={{ flex:1, minWidth:'240px' }}>
        <div style={{ fontSize:'13px', fontWeight:700, color:'#34d399' }}>♻️ Revive old leads — {eligible.length} never booked a demo</div>
        <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', marginTop:'2px' }}>
          {state?.text
            ? <span style={{ color: state.busy ? '#fbbf24' : state.ok ? '#34d399' : '#f87171' }}>{state.text}</span>
            : <>WhatsApp: “{REVIVE_MESSAGE}” {batch.length > 0 && <>· Next batch: {breakdown}</>}</>}
        </div>
      </div>
      <select value={count} onChange={e => setCount(+e.target.value)} disabled={state?.busy} style={{ ...S.btn('ghost'), padding:'6px 10px', fontSize:'12px' }}>
        <option value={25}>Newest 25</option>
        <option value={50}>Newest 50</option>
        <option value={100}>Newest 100</option>
        <option value={0}>All {eligible.length}</option>
      </select>
      <button onClick={send} disabled={state?.busy || !batch.length} style={{ ...S.btn('ghost'), padding:'8px 16px', fontSize:'12px' }}>
        {state?.busy ? '⏳ Sending…' : `📲 Send to ${batch.length}`}
      </button>
    </div>
  )
}

export default function LeadsDashboard({ profile }) {
  const [leads,         setLeads]         = useState([])
  const [loading,       setLoading]       = useState(true)
  const [uploadMsg,     setUploadMsg]     = useState(null)
  const [search,        setSearch]        = useState('')
  const [filterStatus,  setFilterStatus]  = useState('all')
  const [filterDate,    setFilterDate]    = useState('all')
  const [sortBy,        setSortBy]        = useState('date_desc')
  const [viewMode,      setViewMode]      = useState('grouped')
  const [expandedDay,   setExpandedDay]   = useState(null)
  const [dragOver,      setDragOver]      = useState(false)
  const [preview,       setPreview]       = useState(null)
  const [batchSubject,  setBatchSubject]  = useState('')
  const [importing,     setImporting]     = useState(false)
  const [showDupModal,  setShowDupModal]  = useState(false)
  const [dupIds,        setDupIds]        = useState(new Set())
  const [removingDups,  setRemovingDups]  = useState(false)
  const [dupMsg,        setDupMsg]        = useState('')
  const [activeTab,     setActiveTab]     = useState('all')
  const [metaSync,      setMetaSync]      = useState(null)
  const [metaAnswers,   setMetaAnswers]   = useState({})

  // NEW: Form state for manual lead entry
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    course_interest: '',
    teacher_id: '',
    source: 'Manual Entry',
    status: 'New'
  })
  const [teachers, setTeachers] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formMessage, setFormMessage] = useState('')

  const fileRef = useRef()

  useEffect(() => { loadLeads() }, [])

  // Fetch teachers when All Leads tab is active
  useEffect(() => {
    if (activeTab === 'all') {
      fetchTeachers()
    }
  }, [activeTab])

  async function fetchTeachers() {
    try {
      const { data: teachersData } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('role', 'teacher')
        .order('full_name')

      setTeachers(teachersData || [])
    } catch (err) {
      console.error('Error fetching teachers:', err)
    }
  }

  // NEW: Add single lead manually
  async function handleAddLead(e) {
    e.preventDefault()
    
    if (!formData.full_name.trim() || !formData.phone.trim()) {
      setFormMessage({ type: 'err', text: 'Name and phone are required' })
      return
    }

    setIsSubmitting(true)
    setFormMessage(null)

    try {
      const leadData = {
        full_name: formData.full_name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim() || null,
        source: formData.source,
        status: formData.status,
        import_date: new Date().toISOString().slice(0, 10),
        created_at: new Date().toISOString(),
        course_interest: formData.course_interest.trim() || null,
        teacher_id: formData.teacher_id || null,
      }

      const { error } = await supabase
        .from('leads')
        .insert([leadData])

      if (error) {
        setFormMessage({ type: 'err', text: `Error: ${error.message}` })
        return
      }

      setFormMessage({ type: 'ok', text: '✓ Lead added successfully!' })
      setFormData({
        full_name: '',
        phone: '',
        email: '',
        course_interest: '',
        teacher_id: '',
        source: 'Manual Entry',
        status: 'New'
      })
      
      loadLeads()
      
      setTimeout(() => setFormMessage(null), 2000)
    } catch (err) {
      setFormMessage({ type: 'err', text: 'Failed to add lead' })
    } finally {
      setIsSubmitting(false)
    }
  }

  async function loadLeads() {
    setLoading(true)
    const { data } = await supabase.from('leads').select('*')
      .order('import_date', { ascending: false })
      .order('created_at',  { ascending: false })
    const all = data || []
    setLeads(all)
    setDupIds(findDuplicates(all))
    setLoading(false)
    loadMetaAnswers(all)
  }

  // Full form answers for leads that came from Meta lead forms.
  async function loadMetaAnswers(all) {
    const ids = [...new Set(all.map(metaLeadId).filter(Boolean))]
    const byId = {}
    for (let i = 0; i < ids.length; i += 200) {
      const { data } = await supabase.from('meta_leads').select('leadgen_id, raw_field_data').in('leadgen_id', ids.slice(i, i + 200))
      for (const r of data || []) byId[r.leadgen_id] = r.raw_field_data
    }
    setMetaAnswers(byId)
  }

  // ── Duplicate removal ──────────────────────────────────────────────────────
  async function removeAllDuplicates() {
    setRemovingDups(true); setDupMsg('')
    const phoneMap = {}
    const emailMap = {}
    const toDelete = []

    const sorted = [...leads].sort((a,b) => new Date(a.created_at) - new Date(b.created_at))

    sorted.forEach(l => {
      let isDup = false
      if (l.phone && phoneMap[l.phone]) isDup = true
      if (l.email && emailMap[l.email]) isDup = true
      if (isDup) {
        toDelete.push(l.id)
      } else {
        if (l.phone) phoneMap[l.phone] = l.id
        if (l.email) emailMap[l.email] = l.id
      }
    })

    if (!toDelete.length) {
      setDupMsg('No duplicates found!'); setRemovingDups(false); return
    }

    for (let i = 0; i < toDelete.length; i += 10) {
      await supabase.from('leads').delete().in('id', toDelete.slice(i, i+10))
    }

    setDupMsg(`✓ Removed ${toDelete.length} duplicate${toDelete.length!==1?'s':''}!`)
    setShowDupModal(false)
    setRemovingDups(false)
    setTimeout(() => setDupMsg(''), 4000)
    loadLeads()
  }

  // ── Meta lead forms → Leads (api/_metaLeads.js). The 15-minute job does
  // this automatically; the button pulls further back, up to Meta's 90 days.
  async function syncMetaLeadsNow() {
    setMetaSync({ busy: true })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/meta?type=sync-leads&days=30', { headers: { Authorization: `Bearer ${session?.access_token || ''}` } })
      const r = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(r.error || `Sync failed (${res.status})`)
      const errs = r.errors?.length ? ` · ${r.errors.length} form(s) could not be read: ${r.errors[0]}` : ''
      setMetaSync({ ok: !r.errors?.length, text: `✓ ${r.added} new Meta lead${r.added !== 1 ? 's' : ''} added (${r.checked} found in ${r.forms} form${r.forms !== 1 ? 's' : ''} from the last 30 days, ${r.alreadyThere} already here)${errs}` })
      if (r.added) loadLeads()
    } catch (err) {
      setMetaSync({ ok: false, text: err.message })
    }
  }

  // ── File import ────────────────────────────────────────────────────────────
  function handleFile(file) {
    if (!file) return
    if (!file.name.endsWith('.csv')) { setUploadMsg({ type:'err', text:'Please upload a CSV file.' }); return }
    setUploadMsg(null)
    // Meta's "Download leads" CSV is UTF-16 and tab-separated; detect it by
    // its byte-order mark so the columns are read correctly.
    file.slice(0, 2).arrayBuffer().then(buf => {
      const b = new Uint8Array(buf)
      const encoding = (b[0] === 0xFF && b[1] === 0xFE) ? 'UTF-16LE' : (b[0] === 0xFE && b[1] === 0xFF) ? 'UTF-16BE' : 'UTF-8'
      Papa.parse(file, {
        header: true, skipEmptyLines: true, encoding,
        transformHeader: h => h.replace(/^﻿/, '').trim(),
        complete: (results) => {
          if (!results.data?.length) { setUploadMsg({ type:'err', text:'CSV is empty.' }); return }
          const rows = results.data.map(normalise).filter(r => r.phone || (r.full_name && r.full_name !== 'Unknown'))
          if (!rows.length) {
            const cols = (results.meta?.fields || []).slice(0, 12).join(', ')
            setUploadMsg({ type:'err', text:`No leads with a name or phone number found. Columns in this file: ${cols || 'none'}. Expected a name column (Name, full_name or first_name) or a phone column (Phone, phone_number or whatsapp_number).` })
            return
          }
          setPreview(rows)
        },
        error: () => setUploadMsg({ type:'err', text:'Failed to parse CSV.' }),
      })
    })
  }

  function onFileChange(e) { handleFile(e.target.files[0]) }
  function onDrop(e) { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]) }

  async function confirmImport() {
    if (!preview?.length) return
    setImporting(true); setUploadMsg(null)
    let imported = 0, skipped = 0
    try {
      for (const row of preview) {
        const payload = { ...row, course_interest: row.course_interest || batchSubject.trim() || null }
        const { error } = await supabase.from('leads').insert(payload)
        if (error) {
          if (error.code === '23505') skipped++
          else throw error
        } else imported++
      }
      setUploadMsg({ type:'ok', text:`✓ ${imported} leads imported!${skipped > 0 ? ` (${skipped} duplicates skipped)` : ''}` })
      setPreview(null)
      setBatchSubject('')
      if (fileRef.current) fileRef.current.value = ''
      loadLeads()
    } catch (ex) {
      setUploadMsg({ type:'err', text: ex.message || 'Import failed.' })
    }
    setImporting(false)
  }

  async function updateStatus(id, status) {
    await supabase.from('leads').update({ status }).eq('id', id)
    setLeads(prev => prev.map(l => l.id === id ? { ...l, status } : l))
  }

  async function updateSubject(id, course_interest) {
    setLeads(prev => prev.map(l => l.id === id ? { ...l, course_interest } : l))
    await supabase.from('leads').update({ course_interest: course_interest || null }).eq('id', id)
  }

  // Team notes: one line per note, "20 Oct, 2:05 pm · Name: text". Re-reads
  // the lead first so two people adding notes at once don't overwrite each other.
  async function addNote(id, text) {
    const when = new Date().toLocaleString('en-IN', { timeZone:'Asia/Kolkata', day:'numeric', month:'short', hour:'numeric', minute:'2-digit' })
    const line = `${when} · ${profile?.full_name || 'Staff'}: ${text.replace(/\s+/g, ' ')}`
    const { data: fresh } = await supabase.from('leads').select('team_notes').eq('id', id).single()
    const team_notes = [fresh?.team_notes, line].filter(Boolean).join('\n')
    const { error } = await supabase.from('leads').update({ team_notes }).eq('id', id)
    if (error) return window.alert(`Note not saved: ${error.message}`)
    setLeads(prev => prev.map(l => l.id === id ? { ...l, team_notes } : l))
  }

  async function deleteLead(id) {
    if (!window.confirm('Delete this lead?')) return
    await supabase.from('leads').delete().eq('id', id)
    setLeads(prev => prev.filter(l => l.id !== id))
  }

  // ── Filtering ──────────────────────────────────────────────────────────────
  const today   = new Date().toISOString().slice(0,10)
  const weekAgo = new Date(Date.now() - 7*86400000).toISOString().slice(0,10)

  let tabFiltered = leads
  if (activeTab === 'demo') {
    tabFiltered = leads.filter(l => l.source === 'Landing Page')
  }

  // Last calendar month in India time, e.g. '2026-10'.
  const nowIST    = new Date().toLocaleDateString('en-CA', { timeZone:'Asia/Kolkata' })
  const lastMonth = (() => { const [y, m] = nowIST.split('-').map(Number); return m === 1 ? `${y-1}-12` : `${y}-${String(m-1).padStart(2,'0')}` })()
  const leadDay   = l => l.created_at ? new Date(l.created_at).toLocaleDateString('en-CA', { timeZone:'Asia/Kolkata' }) : (l.import_date || '')

  let filtered = tabFiltered.filter(l => {
    if (filterStatus !== 'all' && l.status !== filterStatus) return false
    if (filterDate === 'today' && l.import_date !== today) return false
    if (filterDate === 'week'  && l.import_date < weekAgo) return false
    if (filterDate === 'lastmonth' && !leadDay(l).startsWith(lastMonth)) return false
    if (filterDate === 'dups'  && !dupIds.has(l.id)) return false
    if (search) {
      const q = search.toLowerCase()
      return (l.full_name||'').toLowerCase().includes(q)
          || (l.phone||'').includes(q)
          || (l.email||'').toLowerCase().includes(q)
          || (l.team_notes||'').toLowerCase().includes(q)
    }
    return true
  })

  // ── Sorting ────────────────────────────────────────────────────────────────
  filtered = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case 'date_asc':  return new Date(a.import_date||a.created_at) - new Date(b.import_date||b.created_at)
      case 'name_asc':  return (a.full_name||'').localeCompare(b.full_name||'')
      case 'name_desc': return (b.full_name||'').localeCompare(a.full_name||'')
      case 'status':    return (a.status||'').localeCompare(b.status||'')
      default:          return new Date(b.import_date||b.created_at) - new Date(a.import_date||a.created_at)
    }
  })

  // The leads shown, as a CSV that opens in Excel / Google Sheets (the BOM
  // keeps ₹ and other symbols readable in Excel).
  function downloadLeads() {
    const rows = filtered.map(l => ({
      Date:    leadDay(l),
      Name:    l.full_name || '',
      Subject: l.course_interest || '',
      Email:   l.email || '',
      Phone:   l.phone || l.whatsapp_number || '',
      City:    l.city || formAnswers(l, metaAnswers[metaLeadId(l)]).find(a => /^city$/i.test(a.label))?.value || '',
      Source:  l.source || '',
    }))
    const label = filterDate === 'lastmonth' ? lastMonth : filterDate === 'all' ? 'all' : filterDate
    const blob = new Blob(['﻿' + Papa.unparse(rows)], { type:'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `uniedd-leads-${label}.csv`
    a.click()
    setTimeout(() => URL.revokeObjectURL(a.href), 1000)
  }

  const grouped    = groupByDate(filtered)
  const demoCount  = leads.filter(l => l.source === 'Landing Page').length
  const todayLeads = tabFiltered.filter(l => l.import_date === today).length
  const newLeads   = tabFiltered.filter(l => l.status === 'New').length
  const converted  = tabFiltered.filter(l => l.status === 'Converted' || l.status === 'Enrolled').length
  const dupCount   = tabFiltered.filter(l => dupIds.has(l.id)).length

  const LeadRow = ({ lead }) => (
    <tr
      onMouseEnter={e => e.currentTarget.style.background='rgba(var(--fg-rgb),0.02)'}
      onMouseLeave={e => e.currentTarget.style.background='transparent'}
      style={{ transition:'background 0.1s', background: dupIds.has(lead.id) ? 'rgba(244,163,53,0.04)' : 'transparent' }}>
      <td style={S.td}>
        <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
          {dupIds.has(lead.id) && <span style={{ fontSize:'9px', fontWeight:700, padding:'1px 5px', borderRadius:'4px', background:'rgba(244,163,53,0.2)', color:'#f4a335', flexShrink:0 }}>DUP</span>}
          <div>
            <div style={{ fontWeight:600, color:'rgba(var(--fg-rgb),var(--a90))' }}>{lead.full_name||'—'}</div>
            {lead.stage && <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a25))' }}>{lead.stage}</div>}
          </div>
        </div>
      </td>
      <td style={{ ...S.td, fontFamily:"'DM Mono',monospace", fontSize:'11px' }}>
        {lead.phone ? <a href={`tel:+${lead.phone}`} style={{ color:'#60a5fa', textDecoration:'none' }}>+{lead.phone}</a> : '—'}
      </td>
      <td style={{ ...S.td, fontFamily:"'DM Mono',monospace", fontSize:'11px' }}>
        {(lead.whatsapp_number||lead.phone)
          ? <a href={`https://wa.me/${lead.whatsapp_number||lead.phone}`} target="_blank" rel="noreferrer" style={{ color:'#34d399', textDecoration:'none', display:'flex', alignItems:'center', gap:'4px' }}>💬 +{lead.whatsapp_number||lead.phone}</a>
          : '—'}
      </td>
      <td style={S.td}>
        {lead.email ? <a href={`mailto:${lead.email}`} style={{ color:'rgba(var(--fg-rgb),var(--a60))', textDecoration:'none' }}>{lead.email}</a> : '—'}
      </td>
      <td style={S.td}>
        <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'6px', background:'rgba(167,139,250,0.12)', color:'#a78bfa', fontWeight:600 }}>{lead.source||'—'}</span>
      </td>
      <td style={S.td}>
        <input
          key={lead.id + ':' + (lead.course_interest||'')}
          type="text"
          defaultValue={lead.course_interest || ''}
          placeholder="—"
          onBlur={e => { if (e.target.value !== (lead.course_interest||'')) updateSubject(lead.id, e.target.value.trim()) }}
          style={{ width:'110px', background:'transparent', border:'0.5px solid transparent', borderRadius:'6px', padding:'4px 6px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a75))', outline:'none', fontFamily:"'DM Sans',sans-serif" }}
          onFocus={e => e.target.style.border='0.5px solid rgba(30,144,255,0.3)'}
          onMouseLeave={e => { if (document.activeElement !== e.target) e.target.style.border='0.5px solid transparent' }}
        />
      </td>
      <td style={{ ...S.td, minWidth:'220px', maxWidth:'300px' }}>
        {lead.team_notes && (
          <div style={{ maxHeight:'96px', overflowY:'auto', marginBottom:'4px' }}>
            {lead.team_notes.split('\n').map((line, i) => {
              const [when, ...rest] = line.split(' · ')
              return (
                <div key={i} style={{ fontSize:'11px', lineHeight:1.5 }}>
                  <span style={{ color:'rgba(var(--fg-rgb),var(--a35))' }}>{rest.length ? `${when} · ` : ''}</span>
                  <span style={{ color:'rgba(var(--fg-rgb),var(--a80))' }}>{rest.length ? rest.join(' · ') : line}</span>
                </div>
              )
            })}
          </div>
        )}
        <input
          type="text"
          placeholder="Add a note… (Enter to save)"
          onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { addNote(lead.id, e.target.value.trim()); e.target.value = '' } }}
          style={{ width:'100%', boxSizing:'border-box', background:'rgba(var(--fg-rgb),0.03)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'6px', padding:'5px 7px', fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a80))', outline:'none', fontFamily:"'DM Sans',sans-serif" }}
        />
        {formAnswers(lead, metaAnswers[metaLeadId(lead)]).map((a, i) => (
          <div key={i} style={{ fontSize:'10px', lineHeight:1.5, marginTop: i ? 0 : '4px' }}>
            <span style={{ color:'rgba(var(--fg-rgb),var(--a30))' }}>{a.label}: </span>
            <span style={{ color: /time|booking|slot/i.test(a.label) ? '#f4a335' : 'rgba(var(--fg-rgb),var(--a55))', fontWeight: /time|booking|slot/i.test(a.label) ? 600 : 400 }}>{a.value}</span>
          </div>
        ))}
      </td>
      <td style={S.td}>
        {lead.import_date ? formatDate(lead.import_date) : '—'}
      </td>
      <td style={S.td}>
        <select value={lead.status||'New'} onChange={e => updateStatus(lead.id, e.target.value)}
          style={{ background: STATUS_META[lead.status]?.bg||'rgba(var(--fg-rgb),0.06)', border:`0.5px solid ${STATUS_META[lead.status]?.color||'rgba(var(--fg-rgb),0.1)'}40`, borderRadius:'8px', padding:'4px 8px', fontSize:'11px', color: STATUS_META[lead.status]?.color||'#aaa', fontFamily:"'DM Sans',sans-serif", fontWeight:700, cursor:'pointer', outline:'none' }}>
          {Object.entries(STATUS_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </td>
      <td style={S.td}>
        <div style={{ display:'flex', gap:'5px' }}>
          <a href={`https://wa.me/${lead.whatsapp_number||lead.phone}?text=Hi ${encodeURIComponent(lead.full_name||'there')}, thank you for your interest in UniEDD!`}
            target="_blank" rel="noreferrer"
            style={{ ...S.btn('sm'), textDecoration:'none', padding:'5px 10px', fontSize:'11px', background:'rgba(37,211,102,0.15)', color:'#25d366' }}>WA</a>
          <button onClick={() => deleteLead(lead.id)} style={{ ...S.btn('danger'), padding:'5px 10px', fontSize:'11px' }}>✕</button>
        </div>
      </td>
    </tr>
  )

  const TableHead = () => (
    <thead>
      <tr style={{ background:'rgba(var(--fg-rgb),0.02)' }}>
        {['Name','Phone','WhatsApp','Email','Source','Subject','Notes','Date','Status','Actions'].map(h => <th key={h} style={S.th}>{h}</th>)}
      </tr>
    </thead>
  )

  return (
    <div style={S.wrap}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />

      <div style={{ marginBottom:'20px' }}>
        <div style={{ fontSize:'22px', fontWeight:800, color:'var(--fg-solid)', letterSpacing:'-0.5px' }}>Leads Dashboard</div>
        <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'3px' }}>Upload your Facebook/Instagram CSV — leads save automatically</div>
      </div>

      {/* ── TABS ── */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'20px', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.07)', paddingBottom:'12px' }}>
        <button
          onClick={() => setActiveTab('all')}
          style={{
            padding:'8px 16px',
            background: activeTab === 'all' ? 'rgba(30,144,255,0.15)' : 'transparent',
            border: activeTab === 'all' ? '0.5px solid rgba(30,144,255,0.3)' : 'none',
            borderRadius:'10px',
            color: activeTab === 'all' ? '#60a5fa' : 'rgba(var(--fg-rgb),var(--a50))',
            fontSize:'13px',
            fontWeight:700,
            cursor:'pointer',
            fontFamily:"'DM Sans',sans-serif",
            transition:'all 0.15s'
          }}>
          📋 All Leads ({leads.length})
        </button>
        <button
          onClick={() => setActiveTab('demo')}
          style={{
            padding:'8px 16px',
            background: activeTab === 'demo' ? 'rgba(244,163,53,0.15)' : 'transparent',
            border: activeTab === 'demo' ? '0.5px solid rgba(244,163,53,0.3)' : 'none',
            borderRadius:'10px',
            color: activeTab === 'demo' ? '#f4a335' : 'rgba(var(--fg-rgb),var(--a50))',
            fontSize:'13px',
            fontWeight:700,
            cursor:'pointer',
            fontFamily:"'DM Sans',sans-serif",
            transition:'all 0.15s'
          }}>
          🎬 Demo Bookings ({demoCount})
        </button>
      </div>

      {/* Metrics */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:'10px', marginBottom:'20px' }}>
        {[
          { icon:'📥', label:"Today's",        value: todayLeads,   color:'#60a5fa' },
          { icon:'👥', label:'Total',           value: tabFiltered.length, color:'#a78bfa' },
          { icon:'🔔', label:'New', value: newLeads,     color:'#fbbf24' },
          { icon:'✅', label:'Converted',         value: converted,    color:'#34d399' },
        ].map(m => (
          <div key={m.label} style={S.metric}>
            <div style={{ fontSize:'20px', marginBottom:'8px' }}>{m.icon}</div>
            <div style={{ fontSize:'24px', fontWeight:800, color:m.color, fontFamily:"'DM Mono',monospace" }}>{m.value}</div>
            <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'3px', fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase' }}>{m.label}</div>
          </div>
        ))}
      </div>

      {profile?.role === 'admin' && (
        <div style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap', padding:'12px 16px', background:'rgba(96,165,250,0.06)', border:'0.5px solid rgba(96,165,250,0.2)', borderRadius:'12px', marginBottom:'16px' }}>
          <div style={{ flex:1, minWidth:'200px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color:'#60a5fa' }}>Facebook & Instagram lead forms</div>
            <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', marginTop:'2px' }}>
              {metaSync?.text
                ? <span style={{ color: metaSync.ok ? '#34d399' : '#f87171' }}>{metaSync.text}</span>
                : 'New leads are added here automatically every 15 minutes.'}
            </div>
          </div>
          <button onClick={syncMetaLeadsNow} disabled={metaSync?.busy} style={{ ...S.btn('ghost'), padding:'8px 16px', fontSize:'12px' }}>
            {metaSync?.busy ? '⏳ Syncing…' : '🔄 Sync Meta leads now'}
          </button>
        </div>
      )}

      {profile?.role === 'admin' && <ReviveOldLeads leads={leads} onDone={loadLeads} />}

      <ResultsByAd leads={tabFiltered} />

      {/* Duplicate alert */}
      {dupCount > 0 && (
        <div style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px', background:'rgba(244,163,53,0.08)', border:'1px solid rgba(244,163,53,0.25)', borderRadius:'12px', marginBottom:'16px' }}>
          <span style={{ fontSize:'20px' }}>⚠️</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:'13px', fontWeight:700, color:'#f4a335' }}>{dupCount} duplicate lead{dupCount!==1?'s':''} detected</div>
            <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', marginTop:'2px' }}>Leads sharing the same phone number or email address</div>
          </div>
          <button onClick={() => setShowDupModal(true)} style={{ ...S.btn('warning'), padding:'8px 16px', fontSize:'12px' }}>
            🗑 Remove Duplicates
          </button>
        </div>
      )}

      {dupMsg && <div style={{ padding:'9px 14px', background:'rgba(16,185,129,0.1)', border:'0.5px solid rgba(16,185,129,0.25)', borderRadius:'9px', fontSize:'13px', color:'#34d399', marginBottom:'12px' }}>{dupMsg}</div>}

      {/* NEW: Manual Lead Entry Form - only show on All Leads tab */}
      {activeTab === 'all' && (
        <div style={{ ...S.card, marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'rgba(var(--fg-rgb),var(--a70))', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>➕</span> Add Lead Manually
          </div>
          
          <form onSubmit={handleAddLead} style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <input
                type="text"
                placeholder="Full Name *"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                style={{
                  padding: '10px',
                  border: '1px solid rgba(var(--fg-rgb),0.1)',
                  borderRadius: '8px',
                  background: 'rgba(var(--fg-rgb),0.05)',
                  color: 'rgba(var(--fg-rgb),var(--a80))',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '13px',
                  outline: 'none'
                }}
              />
              <input
                type="tel"
                placeholder="Phone *"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                style={{
                  padding: '10px',
                  border: '1px solid rgba(var(--fg-rgb),0.1)',
                  borderRadius: '8px',
                  background: 'rgba(var(--fg-rgb),0.05)',
                  color: 'rgba(var(--fg-rgb),var(--a80))',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '13px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <input
                type="email"
                placeholder="Email (optional)"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                style={{
                  padding: '10px',
                  border: '1px solid rgba(var(--fg-rgb),0.1)',
                  borderRadius: '8px',
                  background: 'rgba(var(--fg-rgb),0.05)',
                  color: 'rgba(var(--fg-rgb),var(--a80))',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '13px',
                  outline: 'none'
                }}
              />
              <input
                type="text"
                placeholder="Subject (e.g. Guitar, Piano)"
                value={formData.course_interest}
                onChange={(e) => setFormData({ ...formData, course_interest: e.target.value })}
                style={{
                  padding: '10px',
                  border: '1px solid rgba(var(--fg-rgb),0.1)',
                  borderRadius: '8px',
                  background: 'rgba(var(--fg-rgb),0.05)',
                  color: 'rgba(var(--fg-rgb),var(--a80))',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '13px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <select
                value={formData.teacher_id}
                onChange={(e) => setFormData({ ...formData, teacher_id: e.target.value })}
                style={{
                  padding: '10px',
                  border: '1px solid rgba(var(--fg-rgb),0.1)',
                  borderRadius: '8px',
                  background: 'rgba(var(--fg-rgb),0.05)',
                  color: 'rgba(var(--fg-rgb),var(--a80))',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '13px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">Select Teacher</option>
                {teachers.map((teacher) => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.full_name}
                  </option>
                ))}
              </select>
              
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                style={{
                  padding: '10px',
                  border: '1px solid rgba(var(--fg-rgb),0.1)',
                  borderRadius: '8px',
                  background: 'rgba(var(--fg-rgb),0.05)',
                  color: 'rgba(var(--fg-rgb),var(--a80))',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '13px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  padding: '10px 16px',
                  background: isSubmitting ? 'rgba(100,100,100,0.3)' : 'rgba(30,144,255,0.2)',
                  border: '1px solid rgba(30,144,255,0.3)',
                  borderRadius: '8px',
                  color: '#60a5fa',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s'
                }}
              >
                {isSubmitting ? '⏳ Adding...' : '➕ Add Lead'}
              </button>
            </div>

            {formMessage && (
              <div style={{
                padding: '10px 12px',
                borderRadius: '8px',
                fontSize: '13px',
                background: formMessage.type === 'ok' ? 'rgba(52,211,153,0.1)' : 'rgba(239,68,68,0.1)',
                border: `0.5px solid ${formMessage.type === 'ok' ? 'rgba(52,211,153,0.3)' : 'rgba(239,68,68,0.3)'}`,
                color: formMessage.type === 'ok' ? '#34d399' : '#f87171'
              }}>
                {formMessage.text}
              </div>
            )}
          </form>
        </div>
      )}

      {/* Upload - only show on All Leads tab */}
      {activeTab === 'all' && (
        <div style={{ ...S.card, marginBottom:'16px' }}>
          <div style={{ fontSize:'13px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a70))', marginBottom:'12px', display:'flex', alignItems:'center', gap:'8px' }}>
            <span>📤</span> Upload Facebook Leads CSV
          </div>
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            style={{ border:`1.5px dashed ${dragOver ? '#1e90ff' : 'rgba(var(--fg-rgb),0.12)'}`, borderRadius:'12px', padding:'28px 20px', textAlign:'center', cursor:'pointer', background: dragOver ? 'rgba(30,144,255,0.06)' : 'rgba(var(--fg-rgb),0.02)', transition:'all 0.2s' }}
          >
            <div style={{ fontSize:'28px', marginBottom:'8px' }}>📄</div>
            <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a55))', fontWeight:600 }}>{dragOver ? 'Drop it!' : 'Drag & drop CSV here, or click to browse'}</div>
            <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a20))', marginTop:'4px' }}>Supports Facebook Leads CSV format</div>
            <input ref={fileRef} type="file" accept=".csv" onChange={onFileChange} style={{ display:'none' }} />
          </div>

          {uploadMsg && (
            <div style={{ marginTop:'10px', padding:'10px 14px', borderRadius:'10px', fontSize:'13px', background: uploadMsg.type==='ok' ? 'rgba(52,211,153,0.1)' : 'rgba(239,68,68,0.1)', border:`0.5px solid ${uploadMsg.type==='ok' ? 'rgba(52,211,153,0.3)' : 'rgba(239,68,68,0.3)'}`, color: uploadMsg.type==='ok' ? '#34d399' : '#f87171' }}>
              {uploadMsg.text}
            </div>
          )}

          {preview && (
            <div style={{ marginTop:'14px' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px', flexWrap:'wrap', gap:'10px' }}>
                <div style={{ fontSize:'13px', fontWeight:700, color:'#60a5fa' }}>Preview — {preview.length} leads found</div>
                <div style={{ display:'flex', gap:'8px' }}>
                  <button style={S.btn('ghost')} onClick={() => { setPreview(null); setBatchSubject(''); if(fileRef.current) fileRef.current.value='' }}>Cancel</button>
                  <button style={S.btn()} onClick={confirmImport} disabled={importing}>
                    {importing ? 'Importing...' : `Import ${preview.length} Leads`}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom:'10px' }}>
                <label style={{ display:'block', fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a35))', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'5px' }}>Subject (optional)</label>
                <input type="text" value={batchSubject} onChange={e => setBatchSubject(e.target.value)}
                  placeholder="e.g. Guitar — fills in for any row that doesn't already have one in the file"
                  style={{ width:'100%', boxSizing:'border-box', background:'rgba(var(--fg-rgb),0.05)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'8px', padding:'9px 12px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a80))', outline:'none', fontFamily:"'DM Sans',sans-serif" }} />
              </div>

              <div style={{ overflowX:'auto', borderRadius:'10px', border:'0.5px solid rgba(var(--fg-rgb),0.08)' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'520px' }}>
                  <thead>
                    <tr style={{ background:'rgba(var(--fg-rgb),0.03)' }}>
                      {['Name','Phone','WhatsApp','Email','Subject'].map(h => <th key={h} style={S.th}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.slice(0,5).map((r,i) => (
                      <tr key={i}>
                        <td style={S.td}>{r.full_name||'—'}</td>
                        <td style={{ ...S.td, fontFamily:"'DM Mono',monospace", fontSize:'11px' }}>{r.phone||'—'}</td>
                        <td style={{ ...S.td, fontFamily:"'DM Mono',monospace", fontSize:'11px' }}>{r.whatsapp_number||'—'}</td>
                        <td style={S.td}>{r.email||'—'}</td>
                        <td style={S.td}>
                          {r.course_interest || (batchSubject.trim()
                            ? <span style={{ color:'#5aabff' }}>{batchSubject.trim()} <span style={{ color:'rgba(var(--fg-rgb),var(--a25))' }}>(default)</span></span>
                            : '—')}
                        </td>
                      </tr>
                    ))}
                    {preview.length > 5 && <tr><td colSpan={5} style={{ ...S.td, textAlign:'center', color:'rgba(var(--fg-rgb),var(--a25))', fontStyle:'italic' }}>+ {preview.length-5} more</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Filters + Sort bar ── */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'10px', flexWrap:'wrap', alignItems:'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍  Search name, phone, email..."
          style={{ flex:1, minWidth:'180px', background:'rgba(var(--fg-rgb),0.05)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'9px 14px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a80))', outline:'none', fontFamily:"'DM Sans',sans-serif" }} />

        {/* Date filter */}
        <div style={{ display:'flex', gap:'4px' }}>
          {[
            { v:'all',   l:'All Time' },
            { v:'today', l:'Today'    },
            { v:'week',  l:'This Week'},
            { v:'lastmonth', l:new Date(lastMonth + '-01T00:00:00').toLocaleDateString('en-IN', { month:'long' }) },
            { v:'dups',  l:`⚠ Dups (${dupCount})`, warn: dupCount > 0 },
          ].map(d => (
            <button key={d.v} onClick={() => setFilterDate(d.v)} style={{ ...S.btn(filterDate===d.v ? 'primary' : 'ghost'), padding:'8px 12px', fontSize:'11px', background: filterDate===d.v ? (d.warn ? 'rgba(244,163,53,0.3)' : undefined) : undefined, color: filterDate===d.v && d.warn ? '#f4a335' : undefined }}>
              {d.l}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ background:'rgba(var(--fg-rgb),0.06)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'8px 12px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a70))', outline:'none', fontFamily:"'DM Sans',sans-serif", cursor:'pointer' }}>
          <option value="all">All Status</option>
          {Object.entries(STATUS_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>

        <button onClick={downloadLeads} disabled={!filtered.length} title="Download the leads shown (with the filters above) as a sheet that opens in Excel or Google Sheets"
          style={{ ...S.btn('ghost'), padding:'8px 12px', fontSize:'11px' }}>
          ⬇ Download ({filtered.length})
        </button>
      </div>

      {/* Sort + View bar */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'14px', flexWrap:'wrap', alignItems:'center' }}>
        <span style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', fontWeight:600 }}>SORT:</span>
        {[
          { v:'date_desc', l:'📅 Newest First' },
          { v:'date_asc',  l:'📅 Oldest First' },
          { v:'name_asc',  l:'🔤 Name A–Z'     },
          { v:'name_desc', l:'🔤 Name Z–A'     },
          { v:'status',    l:'🏷 Status'        },
        ].map(s => (
          <button key={s.v} onClick={() => setSortBy(s.v)} style={{ ...S.btn(sortBy===s.v?'primary':'ghost'), padding:'7px 12px', fontSize:'11px' }}>{s.l}</button>
        ))}

        <div style={{ marginLeft:'auto', display:'flex', gap:'6px', alignItems:'center' }}>
          <span style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', fontWeight:600 }}>VIEW:</span>
          <button onClick={()=>setViewMode('grouped')} style={{ ...S.btn(viewMode==='grouped'?'primary':'ghost'), padding:'6px 12px', fontSize:'11px' }}>📅 By Date</button>
          <button onClick={()=>setViewMode('flat')}    style={{ ...S.btn(viewMode==='flat'?'primary':'ghost'),    padding:'6px 12px', fontSize:'11px' }}>📋 Flat List</button>
          <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a25))', marginLeft:'6px' }}>{filtered.length} lead{filtered.length!==1?'s':''}</span>
        </div>
      </div>

      {/* ── LEADS TABLE ── */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a30))', fontSize:'13px' }}>Loading leads...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'4rem 2rem', background:'rgba(var(--fg-rgb),0.02)', borderRadius:'16px', border:'0.5px solid rgba(var(--fg-rgb),0.06)' }}>
          <div style={{ fontSize:'40px', marginBottom:'12px' }}>📭</div>
          <div style={{ fontSize:'15px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'6px' }}>No leads found</div>
          <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a20))' }}>
            {search || filterStatus !== 'all' || filterDate !== 'all' ? 'Try adjusting your filters' : activeTab === 'demo' ? 'No demo bookings yet' : 'Upload your Facebook leads CSV above to get started'}
          </div>
        </div>
      ) : viewMode === 'flat' ? (
        // ── FLAT VIEW ──
        <div style={{ ...S.card, padding:'0', overflow:'hidden' }}>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'700px' }}>
              <TableHead />
              <tbody>
                {filtered.map((lead, i) => <LeadRow key={lead.id||i} lead={lead} />)}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        // ── GROUPED BY DATE VIEW ──
        <div style={{ display:'grid', gap:'10px' }}>
          {grouped.map(([dateKey, dayLeads]) => {
            const isToday    = dateKey === today
            const isExpanded = expandedDay === dateKey || isToday
            const newCount   = dayLeads.filter(l => l.status === 'New').length
            const dupInDay   = dayLeads.filter(l => dupIds.has(l.id)).length
            return (
              <div key={dateKey} style={{ ...S.card, padding:'0', overflow:'hidden', border: dupInDay > 0 ? '0.5px solid rgba(244,163,53,0.2)' : undefined }}>
                <div onClick={() => setExpandedDay(isExpanded ? null : dateKey)}
                  style={{ display:'flex', alignItems:'center', gap:'12px', padding:'14px 18px', cursor:'pointer', borderBottom: isExpanded ? '0.5px solid rgba(var(--fg-rgb),0.07)' : 'none', background: isToday ? 'rgba(30,144,255,0.06)' : 'transparent' }}>
                  <div style={{ fontSize:'18px' }}>{isToday ? '🔴' : '📅'}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'14px', fontWeight:700, color: isToday ? '#60a5fa' : 'rgba(var(--fg-rgb),var(--a80))' }}>
                      {isToday ? 'Today' : formatDate(dateKey)}
                      {isToday && <span style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', fontWeight:400, marginLeft:'8px' }}>{dateKey}</span>}
                    </div>
                    <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'2px' }}>
                      {dayLeads.length} lead{dayLeads.length!==1?'s':''}
                      {newCount > 0 && <span style={{ color:'#fbbf24', marginLeft:'6px', fontWeight:700 }}>· {newCount} new</span>}
                      {dupInDay > 0 && <span style={{ color:'#f4a335', marginLeft:'6px', fontWeight:700 }}>· {dupInDay} dup{dupInDay!==1?'s':''}</span>}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:'5px', flexWrap:'wrap', justifyContent:'flex-end' }}>
                    {Object.entries(STATUS_META).map(([s, meta]) => {
                      const count = dayLeads.filter(l => l.status === s).length
                      if (!count) return null
                      return (
                        <span key={s} style={{ ...S.badge(s), fontSize:'9px' }}>
                          <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:meta.dot, display:'inline-block' }} />
                          {count} {meta.label}
                        </span>
                      )
                    })}
                  </div>
                  <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a20))', marginLeft:'4px' }}>{isExpanded?'▲':'▼'}</div>
                </div>

                {isExpanded && (
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'700px' }}>
                      <TableHead />
                      <tbody>
                        {dayLeads.map((lead, i) => <LeadRow key={lead.id||i} lead={lead} />)}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* ── DUPLICATE REMOVAL MODAL ── */}
      {showDupModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
          <div style={{ background:'var(--bg-page)', border:'1px solid rgba(244,163,53,0.3)', borderRadius:'16px', padding:'28px', width:'100%', maxWidth:'420px' }}>
            <div style={{ fontSize:'20px', marginBottom:'8px' }}>⚠️</div>
            <div style={{ fontSize:'16px', fontWeight:800, color:'var(--fg-solid)', marginBottom:'8px' }}>Remove Duplicate Leads</div>
            <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a50))', lineHeight:1.6, marginBottom:'20px' }}>
              Found <strong style={{ color:'#f4a335' }}>{dupCount} leads</strong> with duplicate phone numbers or emails.<br/>
              This will keep the <strong style={{ color:'var(--fg-solid)' }}>oldest entry</strong> and delete all newer duplicates.
            </div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={removeAllDuplicates} disabled={removingDups}
                style={{ flex:1, padding:'12px', background: removingDups ? 'rgba(100,100,100,0.3)' : 'rgba(239,68,68,0.2)', color:'#f87171', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'10px', fontSize:'14px', fontWeight:700, cursor: removingDups ? 'not-allowed' : 'pointer', fontFamily:'inherit' }}>
                {removingDups ? '⏳ Removing...' : '🗑 Yes, Remove Duplicates'}
              </button>
              <button onClick={() => setShowDupModal(false)}
                style={{ padding:'12px 18px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a50))', border:'none', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
